import React, { useEffect, useRef, useState } from "react";
import { clsx } from "clsx";
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";
import {
    ArrowLeftOutlined,
    AudioOutlined,
    LoadingOutlined,
    PlusOutlined,
    ReloadOutlined,
    SendOutlined,
} from "@ant-design/icons";
import { Button, Card, Modal, Spin } from "../../design-system";
import { allowedExtensions, AnalysisKind, IntakeMode, ProjectType } from "../../types/ProjectTypes";
import {
    canSubmit,
    IntakePhase,
    isUploading,
    KIND_CHOICE_MESSAGE,
    KINDS_BY_TYPE,
    MODE_CHOICE_MESSAGE,
    MODE_OPTIONS,
    requirementGroupLabel,
    TYPE_CHOICE_MESSAGE,
    TYPE_CHOICES,
    TYPES,
    unmetRequirementGroups,
    UPLOAD_GREETINGS,
    useGuidedIntake,
} from "./useGuidedIntake";
import {
    Context,
    Documents,
    GuidedSurvey,
    HeaderTitle,
    MessageBubble,
} from "./components";
import type { GuidedSurveyHandle } from "./components";
import {
    INTAKE_TOUR_CHAT_ID,
    INTAKE_TOUR_DOCUMENTS_ID,
    INTAKE_TOUR_NAV_ID,
    INTAKE_TOUR_SUBMIT_ID,
} from "./intakeTourSteps";
import { useChatDemoFlag } from "../demo/demoFlags";
import classes from "./GuidedIntake.module.less";
import { ReactComponent as ContractAnalysisIcon } from "../../assets/icons/svg/contract-analysis-gradient.svg";
import OptionCard from "./components/OptionCard";

// ── 1. Project Type ──────

const TypeChoice: React.FC<{ onChoose: (type: ProjectType) => void }> = ({
    onChoose,
}) => (
    <div className={classes.chooser}>
        <MessageBubble message={TYPE_CHOICE_MESSAGE} />
        <div className={classes.chooserCards}>
            {TYPES.map((type) => {
                const { title, subtitle, Icon } = TYPE_CHOICES[type];
                return (
                    <OptionCard
                        key={type}
                        icon={<Icon />}
                        title={title}
                        subtitle={subtitle}
                        onClick={() => onChoose(type)}
                    />
                );
            })}
        </div>
    </div> 
);

// ── 2. Analysis Kind ──────

/** The kind step's cards: the analysis kinds of the chosen family. */
const KindChoice: React.FC<{
    projectType: ProjectType;
    onChoose: (kind: AnalysisKind) => void;
}> = ({ projectType, onChoose }) => {
    const { Icon } = TYPE_CHOICES[projectType];
    return (
        <div className={classes.chooser}>
            <MessageBubble message={KIND_CHOICE_MESSAGE} />
            <div className={classes.chooserCards}>
                {KINDS_BY_TYPE[projectType].map(({ kind, title, subtitle }) => (
                    <OptionCard
                        key={kind}
                        icon={<Icon />}
                        title={title}
                        subtitle={subtitle}
                        onClick={() => onChoose(kind)}
                    />
                ))}
            </div>
        </div>
    );
};

// ── 3. Upload ──────

const InlineDropzone: React.FC<{ onBrowse: () => void }> = ({ onBrowse }) => (
    <button type="button" className={classes.inlineDropzone} onClick={onBrowse}>
        <ContractAnalysisIcon className={classes.inlineDropzoneIcon} />
        <span className={classes.inlineDropzoneText}>
            Drop documents here, or click to browse
        </span>
    </button>
);

// ── 4. Mode choice ──────

/** After upload: pick how to answer the rest — talk it through or guided questions. */
const ModeChoiceCards: React.FC<{ onChoose: (mode: IntakeMode) => void }> = ({
    onChoose,
}) => (
    <div className={classes.chooserCards}>
        {MODE_OPTIONS.map((opt) => (
            <OptionCard
                key={opt.mode}
                icon={<opt.Icon />}
                title={opt.title}
                subtitle={opt.subtitle}
                tag={opt.tag}
                onClick={() => onChoose(opt.mode)}
            />
        ))}
    </div>
);

// —— Guided Intake Wizard ————

export interface GuidedIntakeViewProps {
    /** When set, resume this existing draft's session instead of starting fresh. */
    projectId?: number;
    /** Called after a successful submit with the created project's id. */
    onSubmitted: (projectId: number) => void;
    /** Reports whether an upload is in flight, so the dialog can block closing. */
    onUploadingChange?: (uploading: boolean) => void;
    /** Called once, when the wizard's initial load is done and it's on screen. */
    onReady?: () => void;
}

export const GuidedIntakeView: React.FC<GuidedIntakeViewProps> = ({
    projectId,
    onSubmitted,
    onUploadingChange,
    onReady,
}) => {
    const {
        state,
        sendMessage,
        attachFiles,
        attachDemoDocuments,
        removeDocument,
        setProjectName,
        commitProjectName,
        chooseType,
        chooseKind,
        confirmUploads,
        chooseMode,
        back,
        startOver,
        updateSurvey,
        submit,
    } = useGuidedIntake(projectId);
    const [draft, setDraft] = useState("");
    const [isDragging, setIsDragging] = useState(false);
    // Chat demo: browse clicks attach scripted documents and the send button
    // plays scripted turns with the composer empty (see demoFlags).
    const [chatDemoEnabled] = useChatDemoFlag();
    // Guided mode: whether the survey is fully answered (gates submit).
    const [surveyComplete, setSurveyComplete] = useState(false);

    // Projections of the intake state this view renders from.
    const loading = state.phase === IntakePhase.Loading;
    const surveyMode = state.phase === IntakePhase.Survey;
    const composerShown = state.phase === IntakePhase.Adaptive;
    // No uploads until the draft exists — it's minted when a kind is picked.
    const uploadsEnabled =
        state.phase !== IntakePhase.Type &&
        state.phase !== IntakePhase.Kind &&
        !loading;
    const uploading = isUploading(state);

    const surveyRef = useRef<GuidedSurveyHandle>(null);
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    // Nested elements fire dragenter/leave; count depth so the overlay only
    // clears when the cursor truly leaves the dialog.
    const dragDepthRef = useRef(0);

    const { transcript, listening, resetTranscript, browserSupportsSpeechRecognition } =
        useSpeechRecognition();

    // Whatever was in the composer when recording started — dictation appends
    // after it rather than replacing it.
    const dictationPrefixRef = useRef("");

    // Stream the interim transcript into the composer while recording; it stays
    // editable so the user can fix it before sending.
    useEffect(() => {
        if (listening) setDraft(dictationPrefixRef.current + transcript);
    }, [transcript, listening]);

    // The browser SpeechRecognition instance is a shared singleton; if the dialog
    // closes mid-recording without aborting, it stays running and the next
    // startListening() no-ops. Abort on unmount so each session starts clean.
    useEffect(() => () => void SpeechRecognition.abortListening(), []);

    // Let the dialog know when uploads are in flight so it can block closing.
    useEffect(() => {
        onUploadingChange?.(uploading);
    }, [uploading, onUploadingChange]);

    // Tell the dialog once the wizard is on screen (the kind step passes back
    // through Loading, so guard against re-firing).
    const readyNotifiedRef = useRef(false);
    useEffect(() => {
        if (loading || readyNotifiedRef.current) return;
        readyNotifiedRef.current = true;
        onReady?.();
    }, [loading, onReady]);

    useEffect(() => {
        const el = messagesContainerRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [state.messages.length, state.phase, state.sending]);

    const stopListening = () => {
        if (listening) void SpeechRecognition.stopListening();
    };

    const toggleMic = () => {
        if (listening) {
            stopListening();
            return;
        }
        resetTranscript();
        const existing = draft.trim();
        dictationPrefixRef.current = existing ? `${existing} ` : "";
        void SpeechRecognition.startListening({ continuous: true, language: "en-US" });
    };

    const handleSend = () => {
        const text = draft.trim();
        // Chat demo: an empty send is the point — it plays the next scripted turn.
        if ((!text && !chatDemoEnabled) || state.sending) return;
        stopListening();
        sendMessage(text);
        setDraft("");
        resetTranscript();
    };

    // What a browse click does: open the file picker, or (chat demo) attach
    // the scripted documents. Used by the dropzone and the "+ Add" button.
    const browseForDocuments = () => {
        if (chatDemoEnabled) attachDemoDocuments();
        else fileInputRef.current?.click();
    };

    const handleAttach = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files ?? []);
        if (files.length) attachFiles(files);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    // Drag-and-drop: accept one or more files dropped anywhere in the dialog —
    // but only once uploads are enabled (after a type is picked).
    const hasFiles = (e: React.DragEvent) =>
        uploadsEnabled && e.dataTransfer.types.includes("Files");

    const handleDragEnter = (e: React.DragEvent) => {
        if (!hasFiles(e)) return;
        e.preventDefault();
        dragDepthRef.current += 1;
        setIsDragging(true);
    };

    const handleDragOver = (e: React.DragEvent) => {
        if (hasFiles(e)) e.preventDefault();
    };

    const handleDragLeave = (e: React.DragEvent) => {
        if (!hasFiles(e)) return;
        dragDepthRef.current -= 1;
        if (dragDepthRef.current <= 0) {
            dragDepthRef.current = 0;
            setIsDragging(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        if (!hasFiles(e)) return;
        e.preventDefault();
        dragDepthRef.current = 0;
        setIsDragging(false);
        const files = Array.from(e.dataTransfer.files);
        if (files.length) attachFiles(files);
    };

    const doSubmit = async () => {
        const submitted = await submit();
        if (submitted && state.projectId != null) onSubmitted(state.projectId);
    };

    const handleSubmit = async () => {
        // Guided mode: block on validation errors (shown inline by the form).
        // The answers themselves live on the hook's state, kept current by
        // updateSurvey — submit() reads them from there.
        if (surveyMode && !surveyRef.current?.validate()) return;
        // Unmet requirements warn (never block): document groups plus any
        // context topics still pending (documents, chat, and survey answers
        // all advance them — nothing in the survey is required).
        const missingGroups = unmetRequirementGroups(state);
        const missingTopics = state.topics.filter((t) => t.status === "pending");
        if (missingGroups.length || missingTopics.length) {
            Modal.confirm({
                title: "Some recommended inputs are missing",
                content: (
                    <>
                        <p>
                            The analysis works best with everything below. You can request
                            it anyway, and an analyst will work with what you provided.
                        </p>
                        <ul>
                            {missingGroups.map((g) => (
                                <li key={g.id}>{requirementGroupLabel(g, state.projectType)}</li>
                            ))}
                            {missingTopics.map((t) => (
                                <li key={t.id}>{t.label}</li>
                            ))}
                        </ul>
                    </>
                ),
                okText: "Request anyway",
                cancelText: "Keep adding",
                onOk: doSubmit,
            });
            return;
        }
        await doSubmit();
    };

    const placeholder = listening
        ? "Recording — speak naturally…"
        : chatDemoEnabled
        ? "Click send to continue the conversation…"
        : "Type a message, or use the mic to speak…";

    // The header's Back / Start over. Back is a pure local step (disabled on the
    // opening step, where there's nowhere to go). Start over deletes the draft —
    // confirmed first, and held back while the submit is in flight.
    const canGoBack = !loading && state.phase !== IntakePhase.Type;
    const canStartOver = state.projectId != null && !loading && !state.submitting;

    const confirmStartOver = () => {
        Modal.confirm({
            title: "Start over",
            content:
                "This deletes the draft, its documents, conversation, and answers, " +
                "and takes you back to the beginning. Are you sure?",
            okText: "Start over",
            okButtonProps: { danger: true },
            onOk: startOver,
        });
    };

    // How many documents have actually landed (uploaded + read).
    const uploadedCount = state.documents.filter((d) => d.documentId != null).length;

    const dropzone = <InlineDropzone onBrowse={browseForDocuments} />;

    // The chat pane's content for the current wizard step. Loading and Survey are
    // handled by the caller (spinner / form); every other phase is a screen: the
    // type cards, the upload flow, the mode cards, or the adaptive transcript.
    const renderChatFlow = () => {
        switch (state.phase) {
            case IntakePhase.Type:
                return (<TypeChoice onChoose={chooseType}/>);
            case IntakePhase.Kind:
                return (<KindChoice projectType={state.projectType!} onChoose={chooseKind}/>);
            case IntakePhase.Upload:
                return (
                    <>
                        {state.analysisKind != null && (
                            <MessageBubble
                                message={UPLOAD_GREETINGS[state.analysisKind]}
                            />
                        )}
                        {dropzone}
                        {state.ackPending ? (
                            // The whole-set read is in flight — a bot message is coming.
                            <MessageBubble
                                message={{
                                    id: "upload-read-back",
                                    kind: "text",
                                    role: "system",
                                    content: "",
                                    typing: true,
                                }}
                            />
                        ) : state.uploadReadBack ? (
                            <>
                                <MessageBubble
                                    message={{
                                        id: "upload-read-back",
                                        kind: "text",
                                        role: "system",
                                        content: state.uploadReadBack,
                                    }}
                                />
                                <div className={classes.uploadFooter}>
                                    <div className={classes.continueRow}>
                                        <Button type="primary" onClick={confirmUploads}>
                                            Continue
                                        </Button>
                                    </div>
                                </div>
                            </>
                        ) : state.uploadProgress == null ? (
                            // Nothing read yet: allow proceeding without documents
                            // (or after an ack failure, with whatever landed).
                            <div className={classes.uploadFooter}>
                                <div className={classes.continueRow}>
                                    <Button type="primary" onClick={confirmUploads}>
                                        {uploadedCount === 0
                                            ? "Continue without documents"
                                            : "Continue"}
                                    </Button>
                                </div>
                            </div>
                        ) : null}
                    </>
                );
            case IntakePhase.Mode:
                return (
                    <div className={classes.chooser}>
                        <MessageBubble message={MODE_CHOICE_MESSAGE} />
                        <ModeChoiceCards onChoose={chooseMode} />
                    </div>
                );
            case IntakePhase.Adaptive:
                return (
                    <>
                        {state.messages.map((m) => (
                            <MessageBubble key={m.id} message={m} />
                        ))}
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <div
            className={classes.page}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            {isDragging && (
                <div className={classes.dropOverlay}>
                    <div className={classes.dropOverlayInner}>Drop files to upload</div>
                </div>
            )}
            {/* One shared hidden file input, opened by both the inline dropzone
                (shown on the Upload step) and the "+ Add" button in the Documents
                box. Kept at the top level so either can trigger it, regardless of
                which one is currently mounted. */}
            <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={allowedExtensions.join(",")}
                style={{ display: "none" }}
                onChange={handleAttach}
            />
            <div className={classes.headerRow}>
                <HeaderTitle
                    value={state.projectName ?? ""}
                    onChange={setProjectName}
                    onCommit={commitProjectName}
                />
                <div className={classes.headerActions} data-tour-id={INTAKE_TOUR_NAV_ID}>
                    <button
                        type="button"
                        className={classes.quietButton}
                        disabled={!canGoBack}
                        onClick={back}
                    >
                        <ArrowLeftOutlined />
                        Back
                    </button>
                    <button
                        type="button"
                        className={classes.quietButton}
                        disabled={!canStartOver}
                        onClick={confirmStartOver}
                    >
                        <ReloadOutlined />
                        Start over
                    </button>
                </div>
            </div>
            <div className={classes.mainGrid}>
                <Card
                    className={classes.chatPane}
                    bordered
                    data-tour-id={INTAKE_TOUR_CHAT_ID}
                >
                    <div className={classes.messages} ref={messagesContainerRef}>
                        {loading ? (
                            <div className={classes.startingState}>
                                <Spin />
                            </div>
                        ) : surveyMode && state.projectId != null ? (
                            <GuidedSurvey
                                ref={surveyRef}
                                projectId={state.projectId}
                                initialResponse={state.survey?.response}
                                onChange={updateSurvey}
                                onValidityChange={setSurveyComplete}
                            />
                        ) : (
                            renderChatFlow()
                        )}
                    </div>

                    {composerShown && (
                    <div className={classes.composer}>
                        <div className={classes.aiNotice}>
                            AI can make mistakes. A human analyst will review your documents.
                        </div>
                        <div
                            className={clsx(
                                classes.inputShell,
                                listening && classes.inputShellRecording
                            )}
                        >
                            <textarea
                                className={classes.textArea}
                                placeholder={placeholder}
                                value={draft}
                                onChange={(e) => setDraft(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSend();
                                    }
                                }}
                                rows={4}
                            />
                            <div className={classes.inputControls}>
                                {browserSupportsSpeechRecognition && (
                                    <button
                                        type="button"
                                        className={clsx(
                                            classes.iconButton,
                                            classes.micButton,
                                            listening && classes.micButtonListening
                                        )}
                                        title={listening ? "Stop recording" : "Tap to record"}
                                        onClick={toggleMic}
                                    >
                                        <AudioOutlined />
                                    </button>
                                )}
                                {listening && (
                                    <div className={classes.listeningHint}>
                                        <span className={classes.waveform}>
                                            <span />
                                            <span />
                                            <span />
                                            <span />
                                            <span />
                                        </span>
                                        Recording…
                                    </div>
                                )}
                                <div className={classes.spacer} />
                                <button
                                    type="button"
                                    className={clsx(classes.iconButton, classes.sendButton)}
                                    title="Send"
                                    onClick={handleSend}
                                    disabled={
                                        (!draft.trim() && !chatDemoEnabled) || state.sending
                                    }
                                >
                                    {state.sending ? <LoadingOutlined /> : <SendOutlined />}
                                </button>
                            </div>
                        </div>
                    </div>
                    )}
                </Card>

                <Card className={classes.checklistPane} bordered>
                    <div
                        className={classes.checklistSection}
                        data-tour-id={INTAKE_TOUR_DOCUMENTS_ID}
                    >
                        <div className={clsx(classes.sectionLabel, classes.docsSectionLabel)}>
                            <span>
                                Documents
                                {uploadedCount > 0 && (
                                    <span className={classes.docsCount}>{uploadedCount}</span>
                                )}
                                {state.uploadProgress && (
                                    <span className={classes.uploadProgress}>
                                        {state.uploadProgress.total === 1
                                            ? "Reading…"
                                            : `Reading ${state.uploadProgress.done}/${state.uploadProgress.total}…`}
                                    </span>
                                )}
                            </span>
                            <button
                                type="button"
                                className={classes.docsAddButton}
                                title={
                                    uploadsEnabled
                                        ? "Add documents"
                                        : "Choose an analysis first"
                                }
                                onClick={browseForDocuments}
                                disabled={!uploadsEnabled}
                            >
                                <PlusOutlined />
                                Add
                            </button>
                        </div>
                        <Documents state={state} onDelete={removeDocument} />
                    </div>

                    <Context state={state} />

                    <div
                        className={classes.reviewButtonWrap}
                        data-tour-id={INTAKE_TOUR_SUBMIT_ID}
                    >
                        <Button
                            type="primary"
                            size="large"
                            block
                            loading={state.submitting}
                            disabled={
                                !canSubmit(state) ||
                                state.submitting ||
                                (surveyMode && !surveyComplete)
                            }
                            onClick={handleSubmit}
                        >
                            Request Analysis
                        </Button>
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default GuidedIntakeView;
